import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { servicesData } from '../../mocks/services';
import { Link, useNavigate } from 'react-router-dom';

const SERVICES_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services`,
      "name": "Tax Preparation & Financial Advisory Services | Bold Plans Advisory Jacksonville FL",
      "description": "Explore Bold Plans Advisory's full range of tax preparation, tax planning, IRS representation, and financial advisory services in Jacksonville, Florida.",
      "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#website` }
    },
    {
      "@type": "Service",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Bold Plans Advisory",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Jacksonville",
          "addressRegion": "FL",
          "addressCountry": "US"
        }
      },
      "areaServed": "Jacksonville, FL",
      "serviceType": "Tax Preparation",
      "name": "Professional Tax Services",
      "description": "Comprehensive tax preparation, planning, IRS representation, and audit support services for individuals and businesses in Jacksonville, Florida."
    }
  ]
};

const ServicesPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Tax Preparation & Financial Advisory Services | Bold Plans Advisory Jacksonville FL';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Explore Bold Plans Advisory\'s full range of tax preparation, tax planning, IRS representation, and financial advisory services in Jacksonville, Florida.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services`);

    const scriptId = 'schema-services';
    document.getElementById(scriptId)?.remove();
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(SERVICES_SCHEMA);
    document.head.appendChild(script);
    return () => { document.getElementById(scriptId)?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">
        <section className="py-24 bg-gradient-to-br from-navy-800 to-navy-700 text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="font-montserrat font-bold text-6xl mb-6">Our Services</h1>
            <p className="text-xl text-white/90 font-sans max-w-3xl mx-auto">
              Comprehensive tax preparation and planning services designed to maximize your refunds and minimize your tax liability
            </p>
          </div>
        </section>

        <section className="py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {servicesData.map((service, index) => (
                <div
                  key={service.id}
                  className="group bg-white border-2 border-gray-100 rounded-3xl p-10 hover:border-orange-500 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="flex items-start gap-6">
                    <div className="w-20 h-20 flex items-center justify-center bg-orange-500/10 rounded-2xl group-hover:bg-orange-500 transition-colors flex-shrink-0">
                      <i className={`${service.icon} text-4xl text-orange-500 group-hover:text-white transition-colors`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-montserrat font-bold text-2xl text-gray-900 mb-4">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 font-sans leading-relaxed mb-6">
                        {service.description}
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-gray-700 font-sans">
                          <i className="ri-check-line text-green-500"></i>
                          <span>Expert consultation included</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700 font-sans">
                          <i className="ri-check-line text-green-500"></i>
                          <span>Personalized strategy development</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700 font-sans">
                          <i className="ri-check-line text-green-500"></i>
                          <span>Ongoing support and guidance</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Tax Calculator CTA */}
            <div className="mt-16 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-12 border-2 border-emerald-200">
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <div className="w-24 h-24 bg-emerald-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-calculator-line text-white text-5xl"></i>
                </div>
                <div className="flex-1 text-center lg:text-left">
                  <h2 className="font-montserrat font-bold text-3xl text-gray-900 mb-3">
                    Free Tax Liability Calculator
                  </h2>
                  <p className="text-gray-700 font-sans text-lg mb-4">
                    Calculate your estimated tax liability instantly with our IRS-compliant calculator. Upload your tax documents or enter information manually to get started.
                  </p>
                  <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-check-line text-emerald-600"></i>
                      <span>IRS-Standard Calculations</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-check-line text-emerald-600"></i>
                      <span>2024 & 2025 Tax Years</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-check-line text-emerald-600"></i>
                      <span>Secure Document Upload</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/tax-calculator')}
                  className="px-8 py-4 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 transition-all shadow-lg whitespace-nowrap"
                >
                  Calculate Now
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>

            {/* Client Portal CTA */}
            <div className="mt-12 bg-gradient-to-br from-teal-50 to-blue-50 rounded-3xl p-12 border-2 border-teal-200">
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <div className="w-24 h-24 bg-teal-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-white text-5xl"></i>
                </div>
                <div className="flex-1 text-center lg:text-left">
                  <h2 className="font-montserrat font-bold text-3xl text-gray-900 mb-3">
                    Secure Client Portal Access
                  </h2>
                  <p className="text-gray-700 font-sans text-lg mb-4">
                    Access your tax documents, proposals, contracts, and invoices 24/7. Upload documents, track progress, and communicate with your tax professional—all in one secure place.
                  </p>
                  <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-check-line text-teal-600"></i>
                      <span>Document Management</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-check-line text-teal-600"></i>
                      <span>E-Signature Capability</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-check-line text-teal-600"></i>
                      <span>Online Payments</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/client-portal')}
                  className="px-8 py-4 bg-teal-600 text-white font-bold rounded-xl hover:bg-teal-700 transition-all shadow-lg whitespace-nowrap"
                >
                  Sign In to Portal
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>

            <div className="mt-16 bg-cream-50 rounded-3xl p-12 text-center">
              <h2 className="font-montserrat font-bold text-4xl text-gray-900 mb-4">
                Not Sure Which Service You Need?
              </h2>
              <p className="text-gray-600 font-sans text-lg mb-8 max-w-2xl mx-auto">
                Schedule a free consultation with our tax experts to discuss your specific needs and find the perfect solution for your situation.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-orange-500 text-white px-8 py-4 rounded-full font-sans font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Schedule Free Consultation
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ServicesPage;
